/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Table, Modal, Button, Spinner } from 'react-bootstrap'
import Chart from 'chart.js/auto'
import axios from 'axios'

// Hooks
import useServices from 'utils/hooks/useServices'

// Services
import MasterServices from 'services/master'

// Components
import { PageTitle } from '../../../../_metronic/layout/core'

const COPDashboardPage: FC = () => {
    const [data] = useServices(MasterServices.getCOPDashboard, {}, [])
    const chartRef = useRef<HTMLCanvasElement | null>(null)
    const chartInstance = useRef<Chart | null>(null)
    
    // State for PDF download
    const [isDownloading, setIsDownloading] = useState(false)
    const [showDownloadModal, setShowDownloadModal] = useState(false)
    const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'success' | 'error'>('idle')
    const [downloadMessage, setDownloadMessage] = useState('')

    const mostViewedCategory = data?.data?.stats_by_category || []
    const mostViewedArticle = data?.data?.stats_by_news || []
    const dailyStats = data?.data?.daily_stats || []
    const monthlyStats = data?.data?.monthly_stats || []

    // Function to handle Excel download (updated for new API response format)
    const handlePDFDownload = async (exportType: 'news' | 'category') => {
        try {
            setIsDownloading(true)
            setShowDownloadModal(true)
            setDownloadStatus('downloading')
            setDownloadMessage('Preparing Excel download...')

            const apiToken = localStorage.getItem('apiToken')
            const baseURL = process.env.REACT_APP_BASE_API_URL
            const endpoint = exportType === 'news' 
                ? `${baseURL}/cop/dashboard/export/stats-by-news`
                : `${baseURL}/cop/dashboard/export/stats-by-category`

            const response = await axios.get(endpoint, {
                headers: {
                    'Authorization': `Bearer ${apiToken}`,
                    'Accept': 'application/json',
                },
                responseType: 'json',
                params: {}
            })

            // Handle the new JSON response format
            if (response.data && response.data.data && response.data.data.file) {
                const base64Data = response.data.data.file.split(',')[1] // Remove the data:application/...;base64, prefix
                const fileName = response.data.data.name || `cop-dashboard-${exportType}-${new Date().toISOString().split('T')[0]}.xlsx`
                
                // Convert base64 to blob
                const byteCharacters = atob(base64Data)
                const byteNumbers = new Array(byteCharacters.length)
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                
                // Create blob URL and trigger download
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                
                // Cleanup
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)

                setDownloadStatus('success')
                setDownloadMessage('Excel file downloaded successfully!')
                
                // Auto close modal after 2 seconds
                setTimeout(() => {
                    setShowDownloadModal(false)
                    setDownloadStatus('idle')
                    setDownloadMessage('')
                }, 2000)
            } else {
                throw new Error('Invalid response format')
            }

        } catch (error) {
            console.error('Download error:', error)
            setDownloadStatus('error')
            setDownloadMessage('Failed to download Excel file. Please try again.')
        } finally {
            setIsDownloading(false)
        }
    }

    // Create chart when data is available
    useEffect(() => {
        if (monthlyStats.length > 0 && chartRef.current) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy()
            }

            const ctx = chartRef.current.getContext('2d')
            if (ctx) {
                const dates = monthlyStats[0]?.details?.map((item: any) => item.date) || []
                const visits = monthlyStats[0]?.details?.map((item: any) => parseInt(item.total_visits)) || []
                const shares = monthlyStats[0]?.details?.map((item: any) => parseInt(item.total_shares)) || []
                
                // Get theme colors
                const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-primary') || '#009ef7'
                const successColor = getComputedStyle(document.documentElement).getPropertyValue('--bs-success') || '#50cd89'

                chartInstance.current = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: dates,
                        datasets: [
                            {
                                label: 'Visits',
                                data: visits,
                                borderColor: primaryColor,
                                backgroundColor: `${primaryColor}20`,
                                tension: 0.4,
                                fill: true
                            },
                            {
                                label: 'Shares',
                                data: shares,
                                borderColor: successColor,
                                backgroundColor: `${successColor}20`,
                                tension: 0.4,
                                fill: true
                            }
                        ]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'top'
                            }
                        },
                        scales: {
                            y: {
                                beginAtZero: true
                            }
                        }
                    }
                })
            }
        }
        
        // Clean up chart when component unmounts
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy()
            }
        }
    }, [monthlyStats])

    return (
        <>
            {/* Download Status Modal */}
            <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Excel Download</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {downloadStatus === 'downloading' && (
                        <>
                            <Spinner animation="border" variant="primary" className="mb-3" />
                            <p>{downloadMessage}</p>
                        </>
                    )}
                    {downloadStatus === 'success' && (
                        <>
                            <div className="text-success mb-3">
                                <i className="fas fa-check-circle fa-3x"></i>
                            </div>
                            <p className="text-success">{downloadMessage}</p>
                        </>
                    )}
                    {downloadStatus === 'error' && (
                        <>
                            <div className="text-danger mb-3">
                                <i className="fas fa-exclamation-circle fa-3x"></i>
                            </div>
                            <p className="text-danger">{downloadMessage}</p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDownloadModal(false)}
                        disabled={downloadStatus === 'downloading'}
                    >
                        {downloadStatus === 'downloading' ? 'Downloading...' : 'Close'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className='row'>
                <div className='col-md-12'>
                    <div className='card-custom'>
                        <h6 className='mb-4'>Key Metrics</h6>
                        <div className='row'>
                            <div className='col-md-8'>
                                <div className='card-custom p-4' style={{height: '300px'}}>
                                    <h6 className='mb-3'>Monthly Activity</h6>
                                    <div style={{position: 'relative', height: '230px'}}>
                                        <canvas ref={chartRef}></canvas>
                                    </div>
                                </div>
                            </div>
                            <div className='col-md-4'>
                                <div className='d-flex flex-column justify-content-start align-items-center gap-4'>
                                    <div className='card-custom p-4 text-center' style={{width: '100%', height: '140px'}}>
                                        <p className='text-gray-600 mb-2 mt-5'>Total Visits</p>
                                        <h6 className='display-6 text-primary'>{dailyStats.visits}</h6>
                                    </div>
                                    <div className='card-custom p-4 text-center' style={{width: '100%', height: '140px'}}>
                                        <p className='text-gray-600 mb-2 mt-5'>Total Shares</p>
                                        <h6 className='display-6 text-primary'>{dailyStats.shares}</h6> 
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <br />       
            <div className='row'>
                <div className='col-md-12'>
                    <div className='card-custom'>
                        <div className='row'>
                            <div className='col-md-6'>
                                <h6>Most Viewed Article</h6>
                            </div>
                            <div className='col-md-6 text-end'>
                                <button 
                                    onClick={() => handlePDFDownload('news')} 
                                    className='link-primary'
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? 'Exporting...' : 'Export'}
                                </button>
                            </div>
                        </div>
                        <Table responsive='sm mt-4'>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Article</th>
                                    <th>Visits</th>
                                    <th>Shares</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mostViewedArticle.map((item: any, index: number) => (
                                    <tr key={index}>
                                        <td>{item.category_name}</td>
                                        <td>{item.news_title}</td>
                                        <td>{item.total_visits}</td>
                                        <td>{item.total_shares}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            <br />

            <div className='row'>
                <div className='col-md-12'>
                    <div className='card-custom'>
                        <div className='row'>
                            <div className='col-md-6'>
                                <h6>Most Viewed Category</h6>
                            </div>
                            <div className='col-md-6 text-end'>
                                <button 
                                    onClick={() => handlePDFDownload('category')} 
                                    className='link-primary'
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? 'Exporting...' : 'Export'}
                                </button>
                            </div>
                        </div>

                        <Table responsive='sm mt-4'>
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th>Visits</th>
                                    <th>Shares</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mostViewedCategory.map((item: any, index: number) => (
                                    <tr key={index}>
                                        <td>{item.category_name}</td>
                                        <td>{item.total_visits}</td>
                                        <td>{item.total_shares}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>  
            </div>
        </>
    )
}   

const COPDashboard: FC = () => (
    <>
        <PageTitle
            breadcrumbs={[
                {
                    title: 'CoP',
                    path: '/cop-dashboard',
                    isSeparator: false,
                    isActive: false,
                },
                {
                    title: '/',
                    path: '',
                    isSeparator: false,
                    isActive: false,
                },
            ]}
        >
            Dashboard
        </PageTitle>
        <COPDashboardPage />
    </>
)

export default COPDashboard